import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { once } from 'lodash';
import BEM from '../../bem';
import Modal from '../Modal';
import mobxInjectSelect from '../../utils/mobxInjectSelect';
import { ReactComponent as PhoneIcon } from '../../../widgets/messenger/images/provider-call-icon.svg';
import { ReactComponent as VideoCallIcon } from '../../images/icon-video.svg';
import { GroupAvatar, RoleAvatar, Spinner, TeamAvatar, UserAvatar } from '../';

const classes = BEM.with('CallActionModal');

function CallActionModal({
  checkAudioAndVideoDevices,
  isGroupVideoCallEnabled,
  isGroupVoiceCallEnabled,
  isOpen,
  isSettingUpCall,
  isVideoCallEnabled,
  isVoiceCallEnabled,
  openModal,
  startCall,
  options,
}) {
  const [buttonSelected, setButtonSelected] = useState('');
  const { entity = {}, videoTrack } = options;
  const { displayName, $entityType: entityType, groupType, p2pRecipient } = entity;
  const isRole = groupType && groupType === 'ROLE_P2P';
  const isGroup = entityType === 'group';
  const isIntraTeam = isGroup && groupType === 'INTRA_TEAM';
  const members = isGroup ? entity.members : [];
  const context = { type: entityType === 'user' ? 'account' : entityType, id: entity.id };
  const recipientRoleId = isRole && p2pRecipient ? p2pRecipient.botUserId : undefined;
  const checkGroupPermissions = isGroup && !isRole;

  const initiateVoiceCall = async () => {
    if (!isOpen || isSettingUpCall) return;
    setButtonSelected('voice');
    startCall({ recipientId: entity.id, recipientRoleId, members, isVideo: false, context });
  };

  const initiateVideoCall = async () => {
    if (!isOpen || isSettingUpCall) return;
    setButtonSelected('video');
    if (!videoTrack) {
      const { videoDevice } = await checkAudioAndVideoDevices();
      openModal('noCamera', { entity, videoDevice });
    } else {
      startCall({ recipientId: entity.id, recipientRoleId, members, isVideo: true, context });
    }
  };

  let avatarFragment;
  if (isIntraTeam) {
    avatarFragment = (
      <TeamAvatar displayName={displayName} fontSize={16} group={entity} size="fit" />
    );
  } else if (isRole && p2pRecipient) {
    avatarFragment = (
      <RoleAvatar
        user={p2pRecipient.members[0]}
        role={p2pRecipient}
        size="fit"
        showPresenceIndicator={true}
        presenceStatus={p2pRecipient.members[0].presenceStatus}
        userId={p2pRecipient.members[0].id}
      />
    );
  } else if (isGroup) {
    avatarFragment = <GroupAvatar group={entity} size="fit" />;
  } else {
    avatarFragment = <UserAvatar user={entity} size="fit" />;
  }

  return (
    <div className={classes('body-container')}>
      {avatarFragment}
      <div className={classes('title')}>
        Call {isRole && p2pRecipient ? p2pRecipient.displayName : displayName}?
      </div>
      {(checkGroupPermissions ? isGroupVoiceCallEnabled : isVoiceCallEnabled) && (
        <button
          className={classNames(classes('call-button'))}
          disabled={isSettingUpCall}
          onClick={once(initiateVoiceCall)}
        >
          {isSettingUpCall && buttonSelected === 'voice' && (
            <div className={classes('spinner')}>
              {' '}
              <Spinner />{' '}
            </div>
          )}
          {!isSettingUpCall &&
            (checkGroupPermissions ? isGroupVoiceCallEnabled : isVoiceCallEnabled) && (
              <div className={classes('call-button-container')}>
                <PhoneIcon className={classes('phone-icon')} />
                <div className={classes('call-button-text')}>
                  {' '}
                  Start {isGroup && !isRole ? 'Group' : ''} Voice Call
                </div>
              </div>
            )}
        </button>
      )}
      {(checkGroupPermissions ? isGroupVideoCallEnabled : isVideoCallEnabled) && (
        <button
          className={classNames(classes('call-button'))}
          disabled={isSettingUpCall}
          onClick={once(initiateVideoCall)}
        >
          {isSettingUpCall && buttonSelected === 'video' && (
            <div className={classes('spinner')}>
              {' '}
              <Spinner />{' '}
            </div>
          )}
          {!isSettingUpCall &&
            (checkGroupPermissions ? isGroupVideoCallEnabled : isVideoCallEnabled) && (
              <div className={classes('call-button-container')}>
                <VideoCallIcon className={classes('video-icon')} />
                <div className={classes('call-button-text')}>
                  {' '}
                  Start {isGroup && !isRole ? 'Group' : ''} Video Call
                </div>
              </div>
            )}
        </button>
      )}
    </div>
  );
}

CallActionModal.propTypes = {
  calleePermissionsCheck: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  isGroupVideoCallEnabled: PropTypes.bool.isRequired,
  isGroupVoiceCallEnabled: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isSettingUpCall: PropTypes.bool.isRequired,
  isVideoCallEnabled: PropTypes.bool.isRequired,
  isVoiceCallEnabled: PropTypes.bool.isRequired,
  startCall: PropTypes.func.isRequired,
  options: PropTypes.shape({
    entity: PropTypes.shape({
      displayName: PropTypes.string.isRequired,
    }).isRequired,
  }),
};

class CallActionModalContainer extends React.Component {
  render() {
    const { closeModal, isOpen } = this.props;

    return (
      <Modal
        bodyClass={classes('body')}
        className={classes()}
        closeClass={classes('close-button')}
        headerClass={classes('header')}
        isOpen={isOpen}
        onRequestClose={closeModal}
        size={'medium'}
      >
        <CallActionModal {...this.props} />
      </Modal>
    );
  }
}

export default mobxInjectSelect({
  callStore: [
    'calleePermissionsCheck',
    'checkAudioAndVideoDevices',
    'closeModal',
    'isGroupVideoCallEnabled',
    'isGroupVoiceCallEnabled',
    'isSettingUpCall',
    'isVideoCallEnabled',
    'isVoiceCallEnabled',
    'startCall',
  ],
  modalStore: ['openModal'],
})(CallActionModalContainer);
